// Las 6 escalas de recuperación anestésica (Aldrete/Bromage/Ramsay/EVA/
// Steward/Otro), reusado 3 veces por `Formulario019.tsx` — uno por momento
// (ingreso/reevaluación/egreso, ver `escalasRecuperacionAnestesica.ts`).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CATEGORIAS_ALDRETE, BROMAGE_OPCIONES, EVA_OPCIONES, puntajeTotalAldrete, RAMSAY_OPCIONES, STEWARD_OPCIONES, type EstadoEscalasRecuperacion } from './escalasRecuperacionAnestesicaDatos';

function CampoSelect({
  id,
  label,
  value,
  opciones,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly opciones: readonly string[];
  readonly onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((op) => (
            <SelectItem key={op} value={op}>
              {op}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export interface EscalasRecuperacionAnestesicaProps {
  readonly titulo: string;
  readonly prefijoId: string;
  readonly estado: EstadoEscalasRecuperacion;
  readonly onChange: (estado: EstadoEscalasRecuperacion) => void;
}

export function EscalasRecuperacionAnestesica({ titulo, prefijoId, estado, onChange }: EscalasRecuperacionAnestesicaProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarAldrete(clave: string, valor: number | ''): void {
    onChange({ ...estado, aldrete: { ...estado.aldrete, [clave]: valor } });
  }

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{titulo}</h4>
      <div className="flex flex-col gap-2">
        <Label>{t('formularios.escalasRecuperacion.aldrete')}</Label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {CATEGORIAS_ALDRETE.map((c) => (
            <div key={c.clave} className="flex flex-col gap-1">
              <Label htmlFor={`${prefijoId}-aldrete-${c.clave}`}>{c.etiqueta}</Label>
              <Input
                id={`${prefijoId}-aldrete-${c.clave}`}
                type="number"
                min={0}
                max={2}
                value={estado.aldrete[c.clave] ?? ''}
                onChange={(e) => actualizarAldrete(c.clave, e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-hce-muted">{t('formularios.f019.puntajeTotal', { puntaje: puntajeTotalAldrete(estado.aldrete) })}</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CampoSelect id={`${prefijoId}-bromage`} label={t('formularios.escalasRecuperacion.bromage')} value={estado.bromage} opciones={BROMAGE_OPCIONES} onChange={(v) => onChange({ ...estado, bromage: v })} />
        <CampoSelect id={`${prefijoId}-ramsay`} label={t('formularios.escalasRecuperacion.ramsay')} value={estado.ramsay} opciones={RAMSAY_OPCIONES} onChange={(v) => onChange({ ...estado, ramsay: v })} />
        <CampoSelect id={`${prefijoId}-eva`} label={t('formularios.escalasRecuperacion.eva')} value={estado.eva} opciones={EVA_OPCIONES} onChange={(v) => onChange({ ...estado, eva: v })} />
        <CampoSelect id={`${prefijoId}-steward`} label={t('formularios.escalasRecuperacion.steward')} value={estado.steward} opciones={STEWARD_OPCIONES} onChange={(v) => onChange({ ...estado, steward: v })} />
        <div className="flex flex-col gap-1 sm:col-span-2">
          <Label htmlFor={`${prefijoId}-otro`}>{t('formularios.escalasRecuperacion.otro')}</Label>
          <Input id={`${prefijoId}-otro`} value={estado.otro} onChange={(e) => onChange({ ...estado, otro: e.target.value })} />
        </div>
      </div>
    </div>
  );
}
